import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { useContext, useEffect, useState } from "react";
import LinkButton from "_shared/Button/LinkButton";
import Button from "_shared/Button/Button";
import SvgIcon from "_shared/SvgIcon/SvgIcon";
import useClickOutsideToClose from "_shared/useClickOutsideToClose";
import { logDefaultHttpError } from "_shared/LogUtils";
import * as i18n from "_shared/i18n";
import { TopBarContext } from "./TopBarContext";
import * as style from "./TopBar.module.css";
const TopBar = () => {
  var _a, _b;
  const {
    latestVersion,
    latestVersionLink,
    isMaintenanceMode,
    areJobsRunning,
    showLicenseExpired,
    showLicenseWarn,
    runningJobs,
    queuedJobs,
    queueEventTarget,
    user,
    version,
    companyName,
    isLogin
  } = useContext(TopBarContext);
  const [showQueue, setShowQueue] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const queueRef = useClickOutsideToClose(showQueue, setShowQueue);
  const userMenuRef = useClickOutsideToClose(showUserMenu, setShowUserMenu);
  const [animatePulseRunning, setAnimatePulseRunning] = useState(false);
  const [animatePulseQueued, setAnimatePulseQueued] = useState(false);
  useEffect(() => {
    function handleNewRunningJob() {
      setAnimatePulseRunning(true);
      setTimeout(() => setAnimatePulseRunning(false), 600);
    }
    queueEventTarget.current.addEventListener("newrunningjob", handleNewRunningJob);
    return () => queueEventTarget.current.removeEventListener("newrunningjob", handleNewRunningJob);
  }, [animatePulseRunning]);
  useEffect(() => {
    function handleNewQueuedJob() {
      setAnimatePulseQueued(true);
      setTimeout(() => setAnimatePulseQueued(false), 600);
    }
    queueEventTarget.current.addEventListener("newqueuedjob", handleNewQueuedJob);
    return () => queueEventTarget.current.removeEventListener("newqueuedjob", handleNewQueuedJob);
  }, [animatePulseQueued]);
  async function interruptRunningJob(job) {
    const url = `/command-center/queue/interruptJob/${job.id}`;
    const response = await fetch(url);
    if (!response.ok) {
      logDefaultHttpError(response.status, url);
      return;
    }
    const json = await response.json();
    if (json.status !== 200) {
      logDefaultHttpError(json.status, url, json.message);
      return;
    }
  }
  async function removeJobFromQueue(job_0) {
    const url_0 = `/command-center/queue/removeJob/${job_0.id}`;
    const response_0 = await fetch(url_0);
    if (!response_0.ok) {
      logDefaultHttpError(response_0.status, url_0);
      return;
    }
    const json_0 = await response_0.json();
    if (json_0.status !== 200) {
      logDefaultHttpError(json_0.status, url_0, json_0.message);
      return;
    }
  }
  async function clearQueue() {
    const url_1 = "/command-center/queue/clear";
    const response_1 = await fetch(url_1);
    if (!response_1.ok) {
      logDefaultHttpError(response_1.status, url_1);
      return;
    }
    const json_1 = await response_1.json();
    if (json_1.status !== 200) {
      logDefaultHttpError(json_1.status, url_1, json_1.message);
      return;
    }
  }
  return /* @__PURE__ */jsxs("div", {
    className: style.topBar,
    children: [/* @__PURE__ */jsxs(LinkButton, {
      color: "tintDark",
      padding: "small",
      href: "/command-center",
      children: [/* @__PURE__ */jsx(SvgIcon, {
        src: "icons/cc-logo-color.svg",
        className: style.logo
      }), /* @__PURE__ */jsx("h1", {
        children: "Command Center"
      })]
    }), /* @__PURE__ */jsxs("div", {
      className: style.itemGroup,
      children: [latestVersion &&
      // latest version of null means up-to-date
      /* @__PURE__ */
      jsx(LinkButton, {
        color: "tintDark",
        padding: "none",
        href: latestVersionLink,
        target: "_blank",
        children: /* @__PURE__ */jsx("div", {
          className: style.latestVersionMessage,
          children: /* @__PURE__ */jsx(i18n.Message, {
            code: "later.version.available",
            args: [latestVersion]
          })
        })
      }), !isLogin && /* @__PURE__ */jsxs(Fragment, {
        children: [isMaintenanceMode && /* @__PURE__ */jsx("div", {
          className: style.maintenanceModeMessage,
          children: /* @__PURE__ */jsx(i18n.Message, {
            code: areJobsRunning ? "system.entering.maintenance.mode" : "system.in.maintenance.mode"
          })
        }), /* @__PURE__ */jsx(LinkButton, {
          color: "tintDark",
          padding: "none",
          label: i18n.getMessage(showLicenseExpired ? "settings.expired.tooltip" : showLicenseWarn ? "settings.warn.tooltip" : "settings.tooltip"),
          className: showLicenseExpired ? style.expiredSettingsButton : showLicenseWarn ? style.warnSettingsButton : "",
          href: "/command-center/settings",
          children: /* @__PURE__ */jsx(SvgIcon, {
            src: "icons/settings.svg",
            alt: i18n.getMessage(showLicenseExpired ? "settings.expired.tooltip" : showLicenseWarn ? "settings.warn.tooltip" : "settings.tooltip")
          })
        }), /* @__PURE__ */jsxs("div", {
          className: style.queueButtonContainer,
          ref: queueRef,
          children: [animatePulseRunning && /* @__PURE__ */jsx("div", {
            className: style.pulseRunning
          }), animatePulseQueued && /* @__PURE__ */jsx("div", {
            className: style.pulseQueued
          }), /* @__PURE__ */jsx(Button, {
            color: "tintDark",
            padding: "none",
            label: i18n.getMessage("queue.and.running.jobs.tooltip"),
            onClick: () => setShowQueue(value => !value),
            children: /* @__PURE__ */jsx(SvgIcon, {
              src: "icons/queue.svg",
              alt: i18n.getMessage("queue.and.running.jobs.tooltip"),
              className: `${(queuedJobs == null ? void 0 : queuedJobs.length) > 1 ? style.multipleQueuedJobs : (queuedJobs == null ? void 0 : queuedJobs.length) > 0 ? style.singleQueuedJob : ""} ${(runningJobs == null ? void 0 : runningJobs.length) > 0 ? style.runningJobs : ""}`
            })
          })]
        }), /* @__PURE__ */jsxs("div", {
          ref: queueRef,
          className: `${style.queue} ${showQueue ? "" : style.hidden} hasScrollBar`,
          children: [/* @__PURE__ */jsxs("div", {
            className: style.queueGroup,
            children: [/* @__PURE__ */jsx("h3", {
              children: /* @__PURE__ */jsx(i18n.Message, {
                code: "running.jobs.title",
                args: [(_a = runningJobs == null ? void 0 : runningJobs.length) != null ? _a : 0]
              })
            }), /* @__PURE__ */jsx("div", {
              className: style.runningJobList,
              children: (runningJobs == null ? void 0 : runningJobs.length) > 0 ? runningJobs.map(job_1 => {
                var _a2;
                return /* @__PURE__ */jsxs("div", {
                  className: style.runningJobItem,
                  children: [/* @__PURE__ */jsx(SvgIcon, {
                    src: job_1.type.icon.src,
                    className: style.jobIcon
                  }), /* @__PURE__ */jsx("span", {
                    children: /* @__PURE__ */jsx(i18n.Message, {
                      code: job_1.type.code,
                      args: [job_1.projectId > 0 ? job_1.projectName : job_1.tmsId > 0 ? job_1.tmsName : ((_a2 = job_1.xtmProjectName) == null ? void 0 : _a2.trim()) ? job_1.xtmProjectName : job_1.systemFileId ? job_1.systemFileId : "ERROR"]
                    })
                  }), /* @__PURE__ */jsx("div", {
                    children: /* @__PURE__ */jsx(LinkButton, {
                      color: "tintDark",
                      size: "small",
                      padding: "none",
                      className: style.hoverButton,
                      href: !job_1.type.isSystem ? `/command-center/project/showProjectJobLog/${job_1.projectId}` : job_1.type.value === "XTM_CALLBACK" ? "/command-center/connections/showLastXtmCallbackJobLog" : job_1.type.value === "CHECK_SEND" ? "/command-center/connections/showLastCheckSendJobLog" : "/command-center/connections/showLastCheckImportJobLog",
                      children: /* @__PURE__ */jsx(SvgIcon, {
                        src: "icons/logs.svg",
                        alt: i18n.getMessage("view.log.from.queue")
                      })
                    })
                  }), /* @__PURE__ */jsx("div", {
                    children: user.role.isAdminOrManager && /* @__PURE__ */jsx(Button, {
                      color: "tintDark",
                      size: "small",
                      padding: "none",
                      className: style.hoverButton,
                      label: i18n.getMessage("kill.running.job"),
                      onClick: () => interruptRunningJob(job_1),
                      children: /* @__PURE__ */jsx(SvgIcon, {
                        src: "icons/delete.svg",
                        alt: i18n.getMessage("kill.running.job")
                      })
                    })
                  })]
                }, job_1.id);
              }) : /* @__PURE__ */jsx("p", {
                className: "color-fg2",
                children: /* @__PURE__ */jsx(i18n.Message, {
                  code: "no.running.jobs"
                })
              })
            })]
          }), /* @__PURE__ */jsxs("div", {
            className: style.queueGroup,
            children: [/* @__PURE__ */jsx("h3", {
              children: /* @__PURE__ */jsx(i18n.Message, {
                code: "queued.jobs.title",
                args: [(_b = queuedJobs == null ? void 0 : queuedJobs.length) != null ? _b : 0]
              })
            }), (queuedJobs == null ? void 0 : queuedJobs.length) > 0 && user.role.isAdminOrManager && /* @__PURE__ */jsx(Button, {
              color: "tintDark",
              size: "small",
              padding: "none",
              className: style.clearQueueButton,
              label: i18n.getMessage("clear.queued.jobs"),
              onClick: clearQueue,
              children: /* @__PURE__ */jsx(SvgIcon, {
                src: "icons/delete.svg",
                alt: i18n.getMessage("clear.queued.jobs")
              })
            }), /* @__PURE__ */jsx("div", {
              className: style.queueList,
              children: (queuedJobs == null ? void 0 : queuedJobs.length) > 0 ? queuedJobs.map(job_2 => {
                var _a2_0;
                return /* @__PURE__ */jsxs("div", {
                  className: style.queuedJobItem,
                  children: [/* @__PURE__ */jsx(SvgIcon, {
                    src: job_2.type.icon.src,
                    className: style.jobIcon
                  }), /* @__PURE__ */jsx("span", {
                    className: style.jobMessageArea,
                    children: /* @__PURE__ */jsx(i18n.Message, {
                      code: job_2.type.code,
                      args: [job_2.projectId > 0 ? job_2.projectName : job_2.tmsId > 0 ? job_2.tmsName : ((_a2_0 = job_2.xtmProjectName) == null ? void 0 : _a2_0.trim()) ? job_2.xtmProjectName : job_2.systemFileId ? job_2.systemFileId : "ERROR"]
                    })
                  }), /* @__PURE__ */jsx("span", {
                    children: user.role.isAdminOrManager && /* @__PURE__ */jsx(Button, {
                      color: "tintDark",
                      size: "small",
                      padding: "none",
                      className: style.hoverButton,
                      label: i18n.getMessage("delete.job.from.queue"),
                      onClick: () => removeJobFromQueue(job_2),
                      children: /* @__PURE__ */jsx(SvgIcon, {
                        src: "icons/delete.svg",
                        alt: i18n.getMessage("delete.job.from.queue")
                      })
                    })
                  })]
                }, job_2.id);
              }) : /* @__PURE__ */jsx("p", {
                className: "color-fg2",
                children: /* @__PURE__ */jsx(i18n.Message, {
                  code: "no.queued.jobs"
                })
              })
            })]
          })]
        }), /* @__PURE__ */jsx("div", {
          ref: userMenuRef,
          children: /* @__PURE__ */jsxs(Button, {
            color: "tintDark",
            padding: "small",
            className: style.userMenuButton,
            onClick: () => setShowUserMenu(value_0 => !value_0),
            children: [/* @__PURE__ */jsx("span", {
              className: style.usernameFlag,
              children: user.username
            }), /* @__PURE__ */jsx("img", {
              className: style.smallAvatar,
              src: "/command-center/assets/default-usericon.png",
              role: "presentation"
            })]
          })
        }), /* @__PURE__ */jsxs("div", {
          ref: userMenuRef,
          className: `${style.userMenu} ${showUserMenu ? "" : style.hidden} hasScrollBar`,
          children: [/* @__PURE__ */jsxs("div", {
            className: style.iconNameRoleContainer,
            children: [/* @__PURE__ */jsx("img", {
              className: style.bigAvatar,
              src: "/command-center/assets/default-usericon.png",
              role: "presentation"
            }), /* @__PURE__ */jsxs("div", {
              className: style.nameRoleContainer,
              children: [/* @__PURE__ */jsx("span", {
                className: style.username,
                children: user.username
              }), /* @__PURE__ */jsx("span", {
                children: /* @__PURE__ */jsx(i18n.Message, {
                  code: user.role.code
                })
              })]
            })]
          }), /* @__PURE__ */jsx(LinkButton, {
            padding: "small",
            href: `/command-center/users/edit/${user.id}`,
            children: /* @__PURE__ */jsx(i18n.Message, {
              code: "default.button.edit.user.label"
            })
          }), /* @__PURE__ */jsx(LinkButton, {
            filled: true,
            padding: "small",
            href: "/command-center/logout",
            children: /* @__PURE__ */jsx(i18n.Message, {
              code: "log.out"
            })
          }), /* @__PURE__ */jsxs("div", {
            className: style.versionAndCompanyNameContainer,
            children: [/* @__PURE__ */jsx("div", {
              className: style.version,
              children: /* @__PURE__ */jsx(i18n.Message, {
                code: "commandcenter.verbose.version",
                args: [version]
              })
            }), /* @__PURE__ */jsx("div", {
              className: style.companyName,
              children: /* @__PURE__ */jsx(i18n.Message, {
                code: "company.name.message",
                args: [companyName]
              })
            })]
          })]
        })]
      })]
    })]
  });
};
export default TopBar;